import { AfterViewInit, Component, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { SolarService } from 'src/app/services/cadastre-solaire/cadastre-solaire.service';
import { SolarPvSurplusService } from 'src/app/services/cadastre-solaire/cadastre-solaire-pv-surplus.service';
import { UsefulService } from 'src/app/services/UsefulService';
import { PvSurplusResult, PvTotalityResult, ThermalResult } from 'src/app/models/Solar';
import { map, Observable, startWith } from 'rxjs';
import { SolarPvTotalityService } from 'src/app/services/cadastre-solaire/cadastre-solaire-pv-totality.service';
import { SolarThermalService } from 'src/app/services/cadastre-solaire/cadastre-solaire-thermal.service';

export type ResultUnion = ThermalResult | PvTotalityResult | PvSurplusResult;

@Component({
    selector: 'cadastreSolaireBankLoan',
    templateUrl: './cadastre-solaire-bank-loan.template.html',
    styleUrls: ['./cadastre-solaire-bank-loan.component.scss'],
    standalone: false,
})
export class CadastreSolaireBankLoanComponent implements OnInit, AfterViewInit {
    @ViewChild('loanInput') loanInput: any;

    public activeCollapseBankLoanSimulation: boolean = false;

    public showMobileUi = false;
    public bankLoanFormGroup: FormGroup;
    public isFormValid: boolean = false;
    public isSimulated: boolean = false;

    public loanStringified: string;
    public durationYearStringified: string;
    public annualInterestRateStringified: string;

    public monthlyPaymentStringified: string;
    public creditCostStringified: string;
    public interestStringified: string;

    public initInvestmentStringified: string;
    public bonusStringified: string;

    public newInvestmentStringified: string;
    public initRoiStringified: string;
    public roiStringified: string;

    private newInvestment: number;
    private interest: number;
    private newRoi: number;

    private defaultDurationYear = 15;
    private defaultAnnualInterestRate = 3.5;

    @Input() investment: number;
    @Input() bonus?: number;
    @Input() gain: number;
    @Input() roi: number;

    loan$!: Observable<number | null>;
    showWarning$!: Observable<boolean | null>;

    public isPvSurplus: boolean = false;
    public isThermal: boolean = false;

    constructor(
        private readonly formBuilder: FormBuilder,
        @Inject(SolarService) public solarService: SolarService,
        @Inject(SolarPvSurplusService)
        public solarPvSurplusService: SolarPvSurplusService,
        public solarPvTotalityService: SolarPvTotalityService,
        public solarThermalService: SolarThermalService,
        @Inject(UsefulService) private usefulService: UsefulService,
    ) {}

    ngOnInit(): void {
        this.isPvSurplus =
            this.solarService.technology === 'pv' && this.solarService.valorization == 'Surplus';
        this.isThermal = this.solarService.technology === 'thermal';

        this.initInvestmentStringified = this.usefulService.stringifyNumber(
            this.usefulService.round(this.investment),
        );

        this.initRoiStringified = this.usefulService.stringifyNumber(
            this.usefulService.round(this.roi),
        );

        if (this.bonus) {
            this.bonusStringified = this.usefulService.stringifyNumber(
                this.usefulService.round(this.bonus),
            );
        }

        this.showMobileUi = this.usefulService.showMobileUi;

        this.bankLoanFormGroup = this.formBuilder.group({
            loan: [null, [Validators.required, Validators.min(1)]],
            durationYear: [this.defaultDurationYear, [Validators.required, Validators.min(1)]],
            annualInterestRate: [
                this.defaultAnnualInterestRate,
                [Validators.required, Validators.min(0), Validators.max(100)],
            ],
        });

        this.bankLoanFormGroup.valueChanges.subscribe((values) => this.checkFormValidity());

        const loanControl = this.bankLoanFormGroup.get('loan')!;
        this.loan$ = loanControl.valueChanges.pipe(startWith(loanControl.value));
        this.showWarning$ = this.loan$.pipe(
            map((loan) => {
                if (!loan) return false;
                return loan > this.usefulService.round(this.investment);
            }),
        );
    }

    ngAfterViewInit() {
        setTimeout(() => {
            this.loanInput?.focus?.();
        });
    }

    public checkFormValidity() {
        this.isFormValid = this.bankLoanFormGroup.status === 'VALID';
    }

    public checkLoanOverInvestment() {}

    public cancel() {
        this.bankLoanFormGroup.reset();
        this.isFormValid = false;
        this.isSimulated = false;

        this.solarService.closeSimulateBankLoan();
    }

    public async simulateBankLoad() {
        if (this.bankLoanFormGroup.valid) {
            const { loan, durationYear, annualInterestRate } = this.bankLoanFormGroup.value;

            const results = await this.solarService.callSimulateBankLoan(
                Number(loan),
                Number(durationYear),
                Number(annualInterestRate) / 100,
                this.investment,
                this.gain,
            );

            const monthlyPayment = results.monthlyPayment;
            const creditCost = results.creditCost;
            this.interest = results.interest;
            this.newInvestment = results.investment;
            this.newRoi = results.roi;

            this.loanStringified = this.usefulService.stringifyNumber(Number(loan));
            this.durationYearStringified = this.usefulService.stringifyNumber(Number(durationYear));
            this.annualInterestRateStringified = this.usefulService.stringifyNumber(
                Number(annualInterestRate),
            );

            this.monthlyPaymentStringified = this.usefulService.stringifyNumber(
                this.usefulService.round(monthlyPayment),
            );
            this.creditCostStringified = this.usefulService.stringifyNumber(
                this.usefulService.round(creditCost),
            );
            this.interestStringified = this.usefulService.stringifyNumber(
                this.usefulService.round(this.interest),
            );
            this.newInvestmentStringified = this.usefulService.stringifyNumber(
                this.usefulService.round(this.newInvestment),
            );
            this.roiStringified = this.usefulService.stringifyNumber(
                this.usefulService.round(this.newRoi),
            );

            this.isSimulated = true;
        }
    }

    public validate() {
        const res = {
            investment: this.newInvestment,
            roi: this.newRoi,
            interest: this.interest,
        };
        this.solarService.setBankLoanResult(res);
    }

    get loanControl() {
        return this.bankLoanFormGroup.get('loan');
    }

    get durationYearControl() {
        return this.bankLoanFormGroup.get('durationYear');
    }

    get annualInterestRateControl() {
        return this.bankLoanFormGroup.get('annualInterestRate');
    }
}
